import { useCallback, useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { faCheck, faEnvelope, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FixDataHygieneEmailParams,
    fixDataHygieneEmail,
    ignoreDataHygieneEmail,
    saveForLaterDataHygieneEmail,
    undoDataHygieneEmail,
    undoSaveForLaterDataHygieneEmail,
} from 'api/contacts/data-hygiene';
import { DataHygieneEmailContext } from 'assets/js/pages/contacts/data-hygiene/DataHygieneEmailContext';
import * as IgnoreButton from 'assets/js/pages/contacts/data-hygiene/IgnoreButton';
import { SaveForLaterButton } from 'assets/js/pages/contacts/data-hygiene/SaveForLaterButton';
import { TableRowSuccessState } from 'assets/js/pages/contacts/data-hygiene/TableRowSuccessState';
import * as Form from 'components/form';
import { TableContext, TableRowContext } from 'components/table/TableContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { DataHygieneEmail } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const SAVE_FOR_LATER_SUCCESS_DURATION = 1000;

const EmailUpdateForm = () => {
    const data = useContext(DataHygieneEmailContext);
    const { provider_suggested, saved_for_later_at, id: emailCheckId } = data.email;
    const { accountId } = useCurrentAccount();
    const { table } = useContext(TableContext);
    const { row } = useContext(TableRowContext);
    const [latestMutationError, setLatestMutationError] = useState<string | null>(null);

    const methods = Form.useGivebutterForm<FixDataHygieneEmailParams>({
        defaultValues: {
            email: provider_suggested ?? '', // NOTE: prefill with provider suggested email (if available)
        },
    });
    const { handleSubmit, control, path } = methods;

    const saveForLaterMutationKey = `email-${emailCheckId}-save-for-later`;

    const handleOnMutate = useCallback(() => {
        if (table.options.enableRowPending) {
            table.addRowPending(row.id);
        }

        setLatestMutationError(null);
    }, [table, row.id]);

    const handleOnError = useCallback((error: GivebutterAxiosError) => {
        setLatestMutationError(error?.response?.data.message ?? 'An unknown error occurred.');
    }, []);

    const handleOnSettled = useCallback(() => {
        if (table.options.enableRowPending) {
            table.removeRowPending(row.id);
        }
    }, [table, row.id]);

    const {
        mutate: saveForLater,
        isPending: saveForLaterIsPending,
        isError: saveForLaterIsError,
    } = useMutation({
        mutationKey: [saveForLaterMutationKey],
        mutationFn: () => {
            return saveForLaterDataHygieneEmail(accountId, emailCheckId);
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            // NOTE: small delay to show success button state (before row gets removed)
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneEmails(accountId),
                });
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
                });
            }, SAVE_FOR_LATER_SUCCESS_DURATION);
        },
    });

    const undoSaveForLaterMutationKey = `email-${emailCheckId}-undo-save-for-later`;

    const {
        mutate: undoSaveForLater,
        isPending: undoSaveForLaterIsPending,
        isError: undoSaveForLaterIsError,
    } = useMutation({
        mutationKey: [undoSaveForLaterMutationKey],
        mutationFn: () => {
            return undoSaveForLaterDataHygieneEmail(accountId, emailCheckId);
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            // NOTE: small delay to show success button state (before row gets removed)
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneEmails(accountId),
                });
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
                });
            }, SAVE_FOR_LATER_SUCCESS_DURATION);
        },
    });

    const {
        mutate,
        isPending: updateIsPending,
        isError,
    } = useMutation<DataHygieneEmail, GivebutterAxiosError, FixDataHygieneEmailParams>({
        mutationFn: values => {
            return fixDataHygieneEmail(accountId, emailCheckId, {
                ...values,
            });
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            if (table.options.enableRowReplacement) {
                table.addRowReplacement({
                    rowId: row.id,
                    maxHeight: 100,
                    content: (
                        <TableRowSuccessState
                            key={emailCheckId}
                            successMessage={`Successfully updated email`}
                            undoMutationFn={() => undoDataHygieneEmail(accountId, emailCheckId)}
                            invalidationKeys={[
                                accountQueryKeys.dataHygieneEmails(accountId),
                                accountQueryKeys.dataHygieneMetrics(accountId),
                            ]}
                        >
                            <Button
                                variant="solid"
                                color="success"
                                size="sm"
                                className="pointer-events-none"
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                Saved
                            </Button>
                        </TableRowSuccessState>
                    ),
                });
            }
        },
    });

    const {
        mutate: ignoreUpdate,
        isPending: ignoreIsPending,
        isError: ignoreIsError,
    } = useMutation({
        mutationFn: () => ignoreDataHygieneEmail(accountId, emailCheckId),
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            if (table.options.enableRowReplacement) {
                table.addRowReplacement({
                    rowId: row.id,
                    maxHeight: 100,
                    content: (
                        <TableRowSuccessState
                            key={emailCheckId}
                            successMessage={`Successfully ignored`}
                            undoMutationFn={() => undoDataHygieneEmail(accountId, emailCheckId)}
                            invalidationKeys={[
                                accountQueryKeys.dataHygieneEmails(accountId),
                                accountQueryKeys.dataHygieneMetrics(accountId),
                            ]}
                        />
                    ),
                });
            }
        },
    });

    const hasLatestMutationError =
        latestMutationError &&
        (isError || ignoreIsError || saveForLaterIsError || undoSaveForLaterIsError);

    const emailIsPending =
        updateIsPending || ignoreIsPending || saveForLaterIsPending || undoSaveForLaterIsPending;

    return (
        <Form.Provider {...methods}>
            <form
                onSubmit={handleSubmit(values => {
                    mutate(values);
                })}
            >
                <div className="flex flex-col gap-2">
                    <Form.Container className="flex flex-col gap-1.5">
                        <h3>
                            <Form.Label
                                name={path('email')}
                                className="block font-medium text-gray-700"
                            >
                                Updated email
                            </Form.Label>
                        </h3>
                        <div>
                            <Form.TextInputRoot
                                name={'email'}
                                rules={{ required: 'Email is required' }}
                                control={control}
                            >
                                <FontAwesomeIcon icon={faEnvelope} className="text-gray-300" />
                                <Form.TextInputForm type={'email'} placeholder={'Enter email'} />
                            </Form.TextInputRoot>
                            <Form.Error name={path('email')} />
                        </div>
                    </Form.Container>

                    {hasLatestMutationError && (
                        <div className="text-sm font-medium text-red-500">
                            {latestMutationError}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-4 pt-2">
                        <SaveForLaterButton
                            disabled={updateIsPending || ignoreIsPending}
                            isSaved={saved_for_later_at !== null}
                            saveForLater={saveForLater}
                            saveForLaterMutationKey={saveForLaterMutationKey}
                            undoSaveForLater={undoSaveForLater}
                            undoSaveForLaterMutationKey={undoSaveForLaterMutationKey}
                        />

                        <div className="flex justify-end gap-2">
                            <IgnoreButton.Root>
                                <IgnoreButton.Button
                                    disabled={emailIsPending}
                                    onClick={() => ignoreUpdate()}
                                >
                                    {ignoreIsPending ? (
                                        <>
                                            <FontAwesomeIcon
                                                className="animate-spin"
                                                icon={faSpinnerThird}
                                            />
                                            Ignoring
                                        </>
                                    ) : (
                                        'Ignore'
                                    )}
                                </IgnoreButton.Button>
                                <IgnoreButton.AlertContent>
                                    <IgnoreButton.AlertBody>
                                        <IgnoreButton.Title />
                                        <IgnoreButton.Description />
                                    </IgnoreButton.AlertBody>
                                    <IgnoreButton.AlertAction onClick={() => ignoreUpdate()} />
                                </IgnoreButton.AlertContent>
                            </IgnoreButton.Root>

                            <Form.SubmitButton
                                disabled={emailIsPending}
                                className="!h-9 !px-3 !py-2 !text-sm"
                            >
                                {updateIsPending ? 'Updating...' : 'Update'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

export { EmailUpdateForm };
